"use client";

import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { Toaster, toast } from "sonner";
import { useSearchParams } from "next/navigation";
import ErrorInputPanel from "./ErrorInputPanel";
import ExplanationOutput from "./ExplanationOutput";
import CommonErrorsPanel from "./CommonErrorsPanel";
import FollowUpPanel from "./FollowUpPanel";
import RecentHistory from "./RecentHistory";
import SiteHeader from "./SiteHeader";
import SiteFooter from "./SiteFooter";
import { ExplanationResult, FollowUpMessage, HistoryItem, SupportedLanguage } from "../types";
import { detectLanguage } from "@/lib/services/languageDetection";

function normalizeLanguageParam(value: string | null): SupportedLanguage | null {
  if (!value) return null;
  const v = value.trim().toLowerCase();
  const aliases: Record<string, SupportedLanguage> = {
    javascript: 'javascript',
    js: 'javascript',
    node: 'javascript',
    nodejs: 'javascript',
    typescript: 'javascript',
    ts: 'javascript',
    python: 'python',
    py: 'python',
    php: 'php',
    java: 'java',
    sql: 'sql',
    other: 'other',
  };

  return aliases[v] ?? null;
}

const DETECTION_DEBOUNCE_MS = 300;

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

const HISTORY_STORAGE_KEY = 'deverror_explainer_history';
const HISTORY_MAX_ITEMS = 10;

interface StoredHistoryItem {
  id: string;
  errorSnippet: string;
  errorMessage: string;
  language: SupportedLanguage;
  timestamp: string;
  explanation: ExplanationResult;
  followUpMessages?: FollowUpMessage[];
}

function loadHistoryFromStorage(): HistoryItem[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(HISTORY_STORAGE_KEY);
    if (!raw) return [];
    const items: StoredHistoryItem[] = JSON.parse(raw);
    return items.map((item) => ({
      ...item,
      timestamp: new Date(item.timestamp),
    }));
  } catch {
    return [];
  }
}

function saveHistoryToStorage(items: HistoryItem[]): void {
  if (typeof window === 'undefined') return;
  try {
    const serialized: StoredHistoryItem[] = items.map((item) => ({
      ...item,
      timestamp: item.timestamp.toISOString(),
    }));
    localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(serialized));
  } catch {
    // localStorage may be full or unavailable
  }
}

export default function ErrorExplainerPage() {
  const searchParams = useSearchParams();
  const source = searchParams.get('source')?.trim()?.toLowerCase();
  const fromBlog = source === 'blog';
  const urlLanguageHint = normalizeLanguageParam(searchParams.get('language'));
  const [errorMessage, setErrorMessage] = useState("");
  const [language, setLanguage] = useState<SupportedLanguage>("javascript");
  const [isLoading, setIsLoading] = useState(false);
  const [explanation, setExplanation] = useState<ExplanationResult | null>(null);
  const [hasError, setHasError] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [requestsUsed, setRequestsUsed] = useState(0);
  const [rateLimit, setRateLimit] = useState(10);
  const [history, setHistory] = useState<HistoryItem[]>(loadHistoryFromStorage);
  const [provider, setProvider] = useState<string | undefined>(undefined);
  const historySynced = useRef(false);
  const [followUpMessages, setFollowUpMessages] = useState<FollowUpMessage[]>([]);
  const [isFollowUpLoading, setIsFollowUpLoading] = useState(false);
  const [followUpError, setFollowUpError] = useState<string | null>(null);
  const [isFollowUpOpen, setIsFollowUpOpen] = useState(false);

  const debouncedErrorMessage = useDebouncedValue(errorMessage, DETECTION_DEBOUNCE_MS);

  const languageDetection = useMemo(
    () =>
      detectLanguage(debouncedErrorMessage, {
        hintLanguage: urlLanguageHint,
        selectedLanguage: language,
        hintWeight: fromBlog ? 3 : 2,
      }),
    [debouncedErrorMessage, urlLanguageHint, language, fromBlog]
  );
  const likelyLanguage = languageDetection.language;
  const hasLanguageMismatch = Boolean(
    likelyLanguage &&
      likelyLanguage !== language &&
      languageDetection.confidence >= (fromBlog ? 3 : 2)
  );

  useEffect(() => {
    const initialError = searchParams.get('error')?.trim();
    const initialLanguage = urlLanguageHint;
    if (initialError) setErrorMessage(initialError);
    if (initialLanguage) {
      setLanguage(initialLanguage);
    }
  }, [searchParams, urlLanguageHint]);

  useEffect(() => {
    const loadUsage = async () => {
      try {
        const res = await fetch('/api/agent/explain-error', { method: 'GET' });
        if (!res.ok) return;
        const data = await res.json();
        if (typeof data?.requestsUsed === 'number') setRequestsUsed(data.requestsUsed);
        if (typeof data?.rateLimit === 'number') setRateLimit(data.rateLimit);
      } catch {
        // non-blocking
      }
    };

    loadUsage();
  }, []);

  useEffect(() => {
    if (!historySynced.current) {
      historySynced.current = true;
      return;
    }
    saveHistoryToStorage(history);
  }, [history]);

  const handleExplain = useCallback(async () => {
    if (!errorMessage.trim()) return;
    if (requestsUsed >= rateLimit) return;

    setIsLoading(true);
    setHasError(false);
    setExplanation(null);
    setProvider(undefined);

    try {
      const res = await fetch('/api/agent/explain-error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          errorMessage,
          language,
          hintLanguage: urlLanguageHint,
          source: fromBlog ? 'blog' : 'explainer',
        }),
      });

      const payload = await res.json();

      if (!res.ok) {
        if (res.status === 429) {
          if (typeof payload?.requestsUsed === 'number') setRequestsUsed(payload.requestsUsed);
          if (typeof payload?.rateLimit === 'number') setRateLimit(payload.rateLimit);
          setHasError(true);
          setErrorText('Daily limit reached (10/day). Please try again after reset.');
          toast.error('Daily limit reached', { description: 'You have used all explainer requests for today.' });
          return;
        }
        throw new Error(payload?.error || 'Failed to generate explanation');
      }

      const resolvedLanguage = normalizeLanguageParam(typeof payload?.language === 'string' ? payload.language : null) || language;
      if (resolvedLanguage !== language) {
        setLanguage(resolvedLanguage);
      }

      const normalizedExplanation = payload?.explanation as ExplanationResult;
      setExplanation(normalizedExplanation);
      setProvider(typeof payload?.provider === 'string' ? payload.provider : undefined);

      if (typeof payload?.requestsUsed === 'number') setRequestsUsed(payload.requestsUsed);
      else setRequestsUsed((prev) => prev + 1);
      if (typeof payload?.rateLimit === 'number') setRateLimit(payload.rateLimit);

      setFollowUpMessages([]);
      setIsFollowUpOpen(false);
      setFollowUpError(null);

      const newHistoryItem: HistoryItem = {
        id: Date.now().toString(),
        errorSnippet: errorMessage.slice(0, 80) + (errorMessage.length > 80 ? "…" : ""),
        errorMessage,
        language: resolvedLanguage,
        timestamp: new Date(),
        explanation: normalizedExplanation,
      };
      setHistory((prev) => [newHistoryItem, ...prev.slice(0, HISTORY_MAX_ITEMS - 1)]);
    } catch (err: any) {
      setHasError(true);
      setErrorText(err?.message || "Failed to generate explanation. Check your connection and try again.");
    } finally {
      setIsLoading(false);
    }
  }, [errorMessage, language, requestsUsed, rateLimit, urlLanguageHint, fromBlog]);

  const handleQuickPick = useCallback((msg: string, lang: SupportedLanguage) => {
    setErrorMessage(msg);
    setLanguage(lang);
    setExplanation(null);
    setProvider(undefined);
    setFollowUpMessages([]);
    setIsFollowUpOpen(false);
  }, []);

  const handleHistorySelect = useCallback((item: HistoryItem) => {
    setErrorMessage(item.errorMessage || item.errorSnippet);
    setLanguage(item.language);
    setExplanation(item.explanation);
    setProvider(undefined);
    setFollowUpMessages(item.followUpMessages || []);
    setIsFollowUpOpen(false);
    setFollowUpError(null);
  }, []);

  const handleUseLikelyLanguage = useCallback(() => {
    if (likelyLanguage) {
      setLanguage(likelyLanguage);
      toast.success(`Language switched to ${likelyLanguage.toUpperCase()}`);
    }
  }, [likelyLanguage]);

  const handleSendFollowUp = useCallback(
    async (question: string) => {
      if (!question.trim() || !errorMessage) return;

      const userMsg: FollowUpMessage = {
        role: 'user',
        content: question.trim(),
      };

      const conversationHistory = [...followUpMessages, userMsg];
      setFollowUpMessages(conversationHistory);
      setIsFollowUpLoading(true);
      setFollowUpError(null);

      try {
        const res = await fetch('/api/agent/explain-error/follow-up', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            errorMessage,
            language,
            conversationHistory,
            question: question.trim(),
          }),
        });

        const payload = await res.json();

        if (!res.ok) {
          if (res.status === 429) {
            setFollowUpError('Daily limit reached. Please try again after reset.');
            if (typeof payload?.requestsUsed === 'number') setRequestsUsed(payload.requestsUsed);
            if (typeof payload?.rateLimit === 'number') setRateLimit(payload.rateLimit);
            return;
          }
          throw new Error(payload?.error || 'Failed to generate answer');
        }

        const assistantMsg: FollowUpMessage = {
          role: 'assistant',
          content: payload.answer,
        };

        const updated = [...conversationHistory, assistantMsg];
        setFollowUpMessages(updated);

        setHistory((historyPrev) =>
          historyPrev.map((item) =>
            item.explanation === explanation
              ? { ...item, followUpMessages: updated }
              : item
          )
        );

        if (typeof payload?.requestsUsed === 'number') setRequestsUsed(payload.requestsUsed);
        if (typeof payload?.rateLimit === 'number') setRateLimit(payload.rateLimit);
      } catch (err: any) {
        setFollowUpError(err?.message || 'Failed to generate follow-up answer');
      } finally {
        setIsFollowUpLoading(false);
      }
    },
    [errorMessage, language, followUpMessages, explanation]
  );

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex flex-col" style={{ fontFamily: "'Plus Jakarta Sans', 'DM Sans', system-ui, sans-serif" }}>
      <Toaster position="bottom-right" theme="dark" />

      {/* Google Fonts */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: #18181b; }
        ::-webkit-scrollbar-thumb { background: #3f3f46; border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: #52525b; }
      `}</style>

      <SiteHeader requestsUsed={requestsUsed} rateLimit={rateLimit} />

      <main className="flex-1 w-full max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16 py-8">
        {/* Hero Section */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full px-4 py-1.5 mb-4">
            <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
            <span className="text-cyan-400 text-xs font-medium tracking-wide uppercase" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
              AI-Powered Debug Assistant
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3 tracking-tight">
            Error Message Explainer
            <span className="text-cyan-400"> for Developers</span>
          </h1>
          <p className="text-zinc-400 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            Paste any programming error, select your language, and get an instant structured explanation —
            what it means, why it happens, and exactly how to fix it.
          </p>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
          {/* Left Column — Input + Output */}
          <div className="xl:col-span-3 2xl:col-span-4 space-y-6">
            <ErrorInputPanel
              errorMessage={errorMessage}
              language={language}
              likelyLanguage={likelyLanguage}
              hasLanguageMismatch={hasLanguageMismatch}
              isLoading={isLoading}
              requestsUsed={requestsUsed}
              rateLimit={rateLimit}
              onErrorMessageChange={setErrorMessage}
              onLanguageChange={setLanguage}
              onUseLikelyLanguage={handleUseLikelyLanguage}
              onExplain={handleExplain}
            />

            {hasError && (
              <div className="bg-red-950/40 border border-red-500/30 rounded-xl p-4 flex items-start gap-3">
                <span className="text-red-400 mt-0.5">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                </span>
                <div className="flex-1">
                  <p className="text-red-300 text-sm">{errorText}</p>
                  {!errorText.toLowerCase().includes('daily limit') && (
                    <button
                      onClick={handleExplain}
                      disabled={isLoading}
                      className="mt-2 text-xs px-3 py-1.5 rounded-lg bg-red-500/15 text-red-300 border border-red-500/25 hover:bg-red-500/25 disabled:opacity-50 transition-colors"
                    >
                      Try again
                    </button>
                  )}
                </div>
              </div>
            )}

            <ExplanationOutput
              explanation={explanation}
              isLoading={isLoading}
              language={language}
              errorMessage={errorMessage}
              provider={provider}
              followUpMessages={followUpMessages}
              onOpenFollowUp={() => setIsFollowUpOpen(true)}
            />

            {explanation && (
              <FollowUpPanel
                messages={followUpMessages}
                isLoading={isFollowUpLoading}
                error={followUpError}
                onSend={handleSendFollowUp}
                isOpen={isFollowUpOpen}
                onToggle={() => setIsFollowUpOpen((v) => !v)}
              />
            )}
          </div>

          {/* Right Column — Common Errors + History */}
          <div className="xl:col-span-1 2xl:col-span-1 space-y-6">
            <CommonErrorsPanel onQuickPick={handleQuickPick} />
            <RecentHistory history={history} onSelect={handleHistorySelect} />
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
